/*
 * Copyright (c) 2024 Pegasystems Inc.
 * All rights reserved.
 */

import { ofType } from 'redux-observable';
import { empty, Observable, of } from 'rxjs';
import { map, mergeMap, switchMap } from 'rxjs/operators';

import { ActionManagerInstance } from '../../actions/manager';
import constants, { publicConstants } from '../../constants';
import { getResolvedTargetName } from '../../router/routing-manager';
import { getTranslatedDXAPIResponse } from '../../utils/dxapi-response-translator';
import { throttleUntilChanged } from '../../utils/rxjs-utils';
import Utils from '../../utils/utils';
import { getRootContainerName } from '../../container/container-utils';
import {
  addPreviewResetAction,
  dispatchCorrectActionIfChildOrRemote,
  getActionBasedOnContainerMode,
  getActionPayload,
  getContext,
  getDirtyDetails,
  processDataToDispatchErrorAction,
  processViewResponse
} from '../../store/state/epics/util';
import { getObjectClassName, getObjectInfoID, getToasterConfig, updateKeyAndFlowName } from '../common-helper';
import { promiseHandler, getDisplayCaseID } from '../../case/common-helper';
import { getSemanticURL } from '../../router/app-router';
import type { Action } from '../../store/state/state-manager';
import { ERROR, ACTION_CREATEOBJECT } from '../../store/state/actions';
import { evaluateDirtyBeforeDxApiCall, STATE_PATHS } from '../../case/create-work/helper';
import { publishDisplayCreatedCaseToasterEvent, publishCreateStageDoneEvent } from '../../case/create-work/events';
import type { ActionsOut } from '../../case/types';
import { isFormValid } from '../../utils/form-handler';
import HeaderProcessor from '../../service_broker/header-processor';
import NavigationUtils from '../../utils/navigation-utils';
import CaseUtils from '../../case/utils';

import {
  getContainerName,
  setDirectUrlUpdation,
  isCreateStageActive,
  getCaseViewMode,
  addActionsForExternalObject,
  canOpenCaseViewAfterCreate,
  canContinueExecution,
  addActionBasedOnContainerMode,
  triggerExternalObjectSubmitEvent,
  resetMappingFlagOnExternalObjectCreation
} from './helper';
import { executeCreateObjectRequest } from './async-request';
import { CREATE_OBJECT } from './action-creator';

const getPayloadContext = (className: string, context: string) => {
  return CaseUtils.isExternalObject(className) && context ? context : getRootContainerName();
};

const createObject = (action$: Observable<Action>) => {
  let actionMgrID = 0;
  let actionsOut: ActionsOut[] = [];
  let toastMessage = {};
  let className: string;
  const throttleValue = 100;

  return action$.pipe(
    ofType(CREATE_OBJECT),
    throttleUntilChanged(throttleValue),
    switchMap((action: Action) => {
      ({ actionMgrID } = action.payload);

      if (CaseUtils.isExternalObject(action.payload.class) && !isFormValid(action.payload.context)) {
        const error = { type: publicConstants.SUBMISSION_FAILURE_TYPE.FORM_VALIDATION_ERROR };
        ActionManagerInstance.rejectActionByID(actionMgrID, true, error);
        // return empty observable as form is not valid.
        return of([]);
      }
      return of(action.payload).pipe(
        mergeMap((payload) => {
          const { context } = payload;
          className = payload.class;
          const containerName = getContainerName(payload);
          const target = getResolvedTargetName(containerName, context);
          const { continueExecution, removeActionPayload } = evaluateDirtyBeforeDxApiCall(target);

          if (!continueExecution) {
            ActionManagerInstance.rejectActionByID(actionMgrID);
            return empty();
          }
          return executeCreateObjectRequest(payload, { removeActionPayload });
        }),
        mergeMap(async (data) => {
          if (data && data.type === ERROR) {
            data.payload.context = getPayloadContext(className, data.payload.context);
            actionsOut.push(processDataToDispatchErrorAction(data));
            return actionsOut;
          }

          const { actionInPayload, response, headers, removeActionPayload } = data;
          const isInCreateStageRes = isCreateStageActive(actionInPayload, response);
          // When a case is created it will be in first assignment
          Utils.setIn(response.data, constants.getProperty(constants.CASE_INFO.IS_FIRST_ASSIGNMENT), true);
          const containerName = getContainerName({ ...actionInPayload, isInCreateStage: isInCreateStageRes });
          const target = getResolvedTargetName(containerName, actionInPayload.context);
          const context = getContext(target, containerName, actionInPayload.context);

          addActionsForExternalObject(actionInPayload, actionsOut);

          if (canContinueExecution(containerName, target, className)) {
            const { continueExecution } = getDirtyDetails(target);
            if (!continueExecution) {
              ActionManagerInstance.rejectActionByID(actionMgrID);
              return empty();
            }
          }
          const openCaseViewRes = actionInPayload?.openCaseViewAfterCreate ?? true;

          if (canOpenCaseViewAfterCreate(openCaseViewRes, isInCreateStageRes)) {
            const { businessID, caseType, resolvedURL } = getToasterConfig(response);
            toastMessage = {
              id: businessID,
              label: caseType,
              href: resolvedURL
            };
            publishDisplayCreatedCaseToasterEvent(toastMessage);
            publishCreateStageDoneEvent({ caseId: businessID, caseType });
            return actionsOut;
          }

          triggerExternalObjectSubmitEvent(actionInPayload);

          const translatedDXAPIResponse = getTranslatedDXAPIResponse(response);
          const ID = String(getDisplayCaseID(getObjectInfoID(translatedDXAPIResponse)));

          let semanticURL = getSemanticURL(ACTION_CREATEOBJECT, {
            caseClassName: getObjectClassName(translatedDXAPIResponse)
          });
          semanticURL = semanticURL.replace(/{.+?}/, ID);
          translatedDXAPIResponse.semanticURL = semanticURL;

          NavigationUtils.setComponentCache(ID, HeaderProcessor.addDebugHeaders(actionInPayload.debugInfo));
          setDirectUrlUpdation(translatedDXAPIResponse, actionInPayload);

          const options = { acName: target, context };

          await processViewResponse(translatedDXAPIResponse, actionInPayload, options);
          const caseViewMode = getCaseViewMode(translatedDXAPIResponse);

          const commonInfo = {
            target,
            context,
            headers,
            semanticURL,
            caseViewMode,
            containerItemID: context,
            isInCreateStage: isInCreateStageRes,
            isMinimizable: isInCreateStageRes,
            assignmentContext: Utils.getIn(translatedDXAPIResponse, STATE_PATHS.NEXT_ASSIGNMENT_INFO_CONTEXT),
            nextAssignmentInfo: Utils.getIn(translatedDXAPIResponse, STATE_PATHS.NEXT_ASSIGNMENT_INFO),
            resourceType: publicConstants.RESOURCE_TYPES.OBJECT,
            openCaseViewAfterCreate: actionInPayload.openCaseViewAfterCreate,
            modalOptions: actionInPayload.modalOptions,
            debugInfo: actionInPayload.debugInfo
          };

          // updating key and flowName
          updateKeyAndFlowName(commonInfo, translatedDXAPIResponse);
          const payload = getActionPayload(translatedDXAPIResponse, commonInfo);
          const actions = getActionBasedOnContainerMode(target, payload);

          addActionBasedOnContainerMode(actions, { removeActionPayload, target }, actionsOut);

          actionsOut.push(actions);
          addPreviewResetAction(actionsOut);
          return actionsOut;
        }),
        map(() => {
          dispatchCorrectActionIfChildOrRemote(actionsOut);
          promiseHandler(actionsOut, actionMgrID);
          resetMappingFlagOnExternalObjectCreation(className);
          toastMessage = {};
          actionsOut = [];
          return actionsOut;
        })
      );
    })
  );
};

export default createObject;
